import React, { useEffect, useRef } from 'react';
import { Box, Typography } from '@mui/material';

import { formatPrice } from '../../lib/money';
import ProductImage from '../storefront/ProductImage';
import { FONT_BODY } from '../storefront/storefrontStyles';

const SPEED_PX_PER_MS = 0.025; // ~25 px/s: se lee mientras pasa
const RESUME_AFTER_MS = 5000;

const prefersReducedMotion = () =>
    typeof window !== 'undefined' &&
    window.matchMedia &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

// Carrusel horizontal con todo el catálogo de la tienda, sobre el video. Se
// desplaza solo; cuando el comprador lo toca, lo arrastra, usa la rueda o le
// pasa el mouse por encima se frena, y vuelve a andar a los 5 s de soltarlo.
// El producto que la tienda está mostrando queda marcado "En pantalla".
const ProductCarousel = ({ products, pinnedProductId, onSelect, sx }) => {
    const scrollRef = useRef(null);
    const pausedRef = useRef(false);
    const hoverRef = useRef(false);
    const positionRef = useRef(0);
    const resumeTimerRef = useRef(null);

    useEffect(() => {
        const el = scrollRef.current;
        if (!el || prefersReducedMotion()) return undefined;

        let frame;
        let last = null;
        const step = (time) => {
            const max = el.scrollWidth - el.clientWidth;
            if (last !== null && !pausedRef.current && max > 0) {
                positionRef.current += SPEED_PX_PER_MS * (time - last);
                // al llegar al final vuelve al principio
                if (positionRef.current >= max) positionRef.current = 0;
                el.scrollLeft = positionRef.current;
            }
            last = time;
            frame = requestAnimationFrame(step);
        };
        frame = requestAnimationFrame(step);

        return () => cancelAnimationFrame(frame);
    }, [products.length]);

    useEffect(() => () => clearTimeout(resumeTimerRef.current), []);

    const pause = () => {
        pausedRef.current = true;
        clearTimeout(resumeTimerRef.current);
        resumeTimerRef.current = setTimeout(() => {
            if (hoverRef.current) return;
            // retoma desde donde lo dejó el comprador
            if (scrollRef.current) {
                positionRef.current = scrollRef.current.scrollLeft;
            }
            pausedRef.current = false;
        }, RESUME_AFTER_MS);
    };

    // sólo el mouse "se queda encima": en touch el pointerenter llega con el
    // toque y nunca hay un leave que lo libere
    const onPointerEnter = (event) => {
        if (event.pointerType !== 'mouse') return;
        hoverRef.current = true;
        pause();
    };
    const onPointerLeave = (event) => {
        if (event.pointerType !== 'mouse') return;
        hoverRef.current = false;
        pause();
    };

    if (!products || products.length === 0) return null;

    return (
        <Box
            ref={scrollRef}
            onPointerEnter={onPointerEnter}
            onPointerLeave={onPointerLeave}
            onPointerDown={pause}
            // el visor de vivos cambia de transmisión con un swipe horizontal:
            // arrastrar el carrusel no tiene que llegar hasta ahí
            onTouchStart={(event) => {
                event.stopPropagation();
                pause();
            }}
            onTouchEnd={(event) => event.stopPropagation()}
            onWheel={pause}
            sx={{
                display: 'flex',
                gap: 1,
                overflowX: 'auto',
                px: 1.5,
                py: 0.5,
                scrollbarWidth: 'none',
                '&::-webkit-scrollbar': { display: 'none' },
                ...sx,
            }}
        >
            {products.map((product) => {
                const pinned =
                    pinnedProductId != null &&
                    String(product.productId) === String(pinnedProductId);
                const image = product.images && product.images[0];
                return (
                    <Box
                        key={product.productId}
                        component="button"
                        type="button"
                        onClick={() => onSelect(product)}
                        aria-label={`Ver ${product.name}`}
                        sx={{
                            flexShrink: 0,
                            width: 104,
                            p: 0,
                            textAlign: 'left',
                            cursor: 'pointer',
                            borderRadius: '10px',
                            overflow: 'hidden',
                            position: 'relative',
                            backgroundColor: 'rgba(20,20,28,0.85)',
                            border: pinned
                                ? '2px solid #29D2AB'
                                : '1px solid rgba(255,255,255,0.15)',
                            color: 'white',
                        }}
                    >
                        <ProductImage
                            src={image && image.url}
                            alt={product.name}
                            sx={{ paddingTop: '75%' }}
                        />
                        {pinned && (
                            <Box
                                sx={{
                                    position: 'absolute',
                                    top: 4,
                                    left: 4,
                                    backgroundColor: '#29D2AB',
                                    color: '#0F1B2D',
                                    fontSize: 9,
                                    fontWeight: 800,
                                    fontFamily: FONT_BODY,
                                    borderRadius: '4px',
                                    px: 0.6,
                                    py: 0.1,
                                    textTransform: 'uppercase',
                                }}
                            >
                                En pantalla
                            </Box>
                        )}
                        <Box sx={{ px: 0.75, py: 0.5 }}>
                            <Typography
                                sx={{
                                    fontSize: 11,
                                    fontFamily: FONT_BODY,
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                }}
                            >
                                {product.name}
                            </Typography>
                            <Typography
                                sx={{
                                    fontSize: 12,
                                    fontWeight: 700,
                                    fontFamily: FONT_BODY,
                                }}
                            >
                                {formatPrice(product.price)}
                            </Typography>
                        </Box>
                    </Box>
                );
            })}
        </Box>
    );
};

export default ProductCarousel;
